import React from 'react';
import { Box, Typography, Stack, TextField } from '@mui/material';

// -------------------------------
// HELPERS
// -------------------------------
const totalSecondsToHms = (total = 0) => {
    const h = Math.floor(total / 3600);
    const r = total % 3600;
    const m = Math.floor(r / 60);
    const s = r % 60;
    return { h, m, s };
};

const hmsToTotalSeconds = ({ h = 0, m = 0, s = 0 }) =>
    (Number(h) || 0) * 3600 +
    (Number(m) || 0) * 60 +
    (Number(s) || 0);

// -------------------------------
// COMPONENTE
// -------------------------------
function TimeInput({
    label,
    name,
    value = 0,
    onChange,
    mode = 'hms', // 'hms' | 'ms' | 'm' | 's'
    min = 0,
    max = 86400
}) {

    const timeObj = totalSecondsToHms(value);

    // -------------------------------
    // ON CHANGE (permite vacío temporal)
    // -------------------------------
    const handleChange = (field) => (e) => {
        const val = e.target.value;

        const updated = {
            ...timeObj,
            [field]: val === '' ? '' : parseInt(val, 10)
        };

        const total = hmsToTotalSeconds(updated);

        onChange({
            target: { name, value: total }
        });
    };

    // -------------------------------
    // ON BLUR (VALIDACIÓN REAL)
    // -------------------------------
    const handleBlur = (field) => (e) => {
        let val = parseInt(e.target.value, 10);

        if (isNaN(val)) val = 0;

        const maxVal = field === 'h' ? 23 : 59;
        val = Math.max(0, Math.min(val, maxVal));

        const updated = {
            ...timeObj,
            [field]: val
        };

        let total = hmsToTotalSeconds(updated);

        // 🔒 aplicar límites globales
        if (total < min) total = min;
        if (total > max) total = max;

        onChange({
            target: { name, value: total }
        });
    };

    // -------------------------------
    // RENDER
    // -------------------------------
    return (
        <Box
            sx={{
                mb: 3,
                border: '1px solid #ccc',
                borderRadius: '4px',
                p: 2,
                position: 'relative'
            }}
        >
            {/* LABEL FLOTANTE */}
            <Typography
                variant="caption"
                sx={{
                    position: 'absolute',
                    top: 0,
                    left: 12,
                    transform: 'translateY(-50%)',
                    bgcolor: 'background.paper',
                    px: 1,
                    color: 'text.secondary'
                }}
            >
                {label}
            </Typography>

            <Stack direction="row" spacing={1} alignItems="center" justifyContent="center">

                {/* HORAS */}
                {(mode === 'hms' || mode === 'hm' || mode === 'h') && (
                    <>
                        <TextField
                            label="HH"
                            type="number"
                            value={String(timeObj.h ?? 0).padStart(2, '0')}
                            onChange={handleChange('h')}
                            onBlur={handleBlur('h')}
                            size="small"
                            sx={{ width: 75 }}
                            inputProps={{ style: { textAlign: 'center' } }}
                        />
                        <Typography variant="h6">:</Typography>
                    </>
                )}

                {/* MINUTOS */}
                {(mode === 'hms' || mode === 'ms' || mode === 'm') && (
                    <>
                        <TextField
                            label="MM"
                            type="number"
                            value={String(timeObj.m ?? 0).padStart(2, '0')}
                            onChange={handleChange('m')}
                            onBlur={handleBlur('m')}
                            size="small"
                            sx={{ width: 75 }}
                            inputProps={{ style: { textAlign: 'center' } }}
                        />
                    </>
                )}

                {/* SEGUNDOS */}
                {(mode === 'hms' || mode === 'ms' | mode === 's') && (
                    <>
                        <Typography variant="h6">{mode === 's' ? "" : ":"}</Typography>
                        <TextField
                            label="SS"
                            type="number"
                            value={String(timeObj.s ?? 0).padStart(2, '0')}
                            onChange={handleChange('s')}
                            onBlur={handleBlur('s')}
                            size="small"
                            sx={{ width: 75 }}
                            inputProps={{ style: { textAlign: 'center' } }}
                        />
                    </>
                )}
            </Stack>
        </Box>
    );
}

export default TimeInput;
